import {
  createContext,
  FC,
  memo,
  useCallback,
  useContext,
  useMemo,
  useState,
  useEffect,
  useRef,
} from 'react';
import useAsyncEffect from 'use-async-effect';

import { SCREENS, AWAITING_RESPONSE_TYPE } from '@core/assistant/interfaces';
import { debugEventId } from '@core/assistant/constants';
import { PollyLanguage } from '@shared/interfaces/polly';
import { assistantMapKey } from '@shared/utils/assistantMap';
import { IEventSettingsAiAssistant } from '@interfaces/event';
import { TriggerType } from '@interfaces/assistantScript';
import { teleportToRegion as teleportToRegionFn } from './providerV1';
import { getCatchErrorMessage } from '@helpers/error';

import {
  IAssistantContext,
  ProviderProps,
  SetOpenAutoResult,
} from './interfaces';
import { initDebugSendFn, log } from './Debug';
import { globals } from './globals';
import { LinearScriptPlayer } from './scriptPlayer/linearScriptPlayer';
import { resolveJoinTriggerName, buildParentTriggerMap } from './providerUtils';

import { useAssistantNavigation } from './useAssistantNavigation';
import { useAssistantChat, GeneratePollyAudioFn } from './useAssistantChat';
import { useAssistantProgress } from './useAssistantProgress';
import {
  useAssistantScripts,
  GetEffectiveAiSettingsFn,
} from './useAssistantScripts';
import {
  useAssistantAudio,
  ScriptTransitionCallbacks,
} from './useAssistantAudio';
import {
  useScriptFlow,
  PlayBlockAudioFn,
  CbOnCompletedStepFn,
  StartAudioPrerenderFn,
  StartFlowFn,
} from './useScriptFlow';
import { useRegionHandler } from './useRegionHandler';

export const AssistantContext = createContext<IAssistantContext>(
  {} as IAssistantContext,
);

export const useAssistant = () => useContext(AssistantContext);

const defaultAvailableLanguages: PollyLanguage[] = [
  PollyLanguage.ENGLISH,
  PollyLanguage.FRENCH,
  PollyLanguage.ITALIAN,
  PollyLanguage.GERMAN,
  PollyLanguage.ARABIC,
  PollyLanguage.CHINESE,
  PollyLanguage.SPANISH,
];

const defaultAiSettings: IEventSettingsAiAssistant = {
  engine: 'generative',
  voiceId: 'Ruth',
  language: PollyLanguage.ENGLISH,
};

const AssistantProviderInner: FC<ProviderProps> = ({
  children,
  currentRegionName,
  userName,
  mytaverseEventRef,
  sendWSRef,
  selectedLanguageRef,
  selectedLanguage,
  mytaverseUserRef,
  objectPoiId,
  assistantScriptUpdateTick,
  activeBrandingId,
  assistantSound,
  pendingAssistantRegionTrigger,
  isAskMeAnythingEnabled,
}) => {
  const [open, setOpen] = useState(false);
  const [spotTriggerType, setSpotTriggerType] = useState<TriggerType | null>(
    null,
  );
  const [isSpotImageModalOpen, setIsSpotImageModalOpen] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isMuted, setIsMutedLoc] = useState(false);
  const [audioLanguage, setAudioLanguage] = useState<PollyLanguage>(
    PollyLanguage.ENGLISH,
  );

  const [audioPromptHistory] = useState<string[]>([]);

  useEffect(() => {
    const gain = assistantSound ?? 1;
    globals.assistantGain = gain;
    try {
      globals.visualizerInstance?.setGain(gain);
    } catch {
      // visualizer instance may already be torn down — ignore
    }
  }, [assistantSound]);

  const sendJSONMessageToWebSocketRef = sendWSRef;

  const openRef = useRef(open);
  openRef.current = open;

  // Returns whether the assistant actually opened. Callers that are about to
  // start playing audio must check this before proceeding — otherwise a
  // suppressed auto-open leaves audio playing with no visible Noa, which
  // reads as broken. See useAssistantAudio.playBlockAudio and
  // useScriptFlow.startFlow (ui_click branch).
  // Gated on isEditorInspectorOpen (the right-pane scripts/knowledge
  // Inspector), not isBrandingModeEnabled — AssistantWrapper's showAssistantUi
  // (index.tsx) only hides Noa's UI while that pane actually occupies the
  // screen, so being in Editor Mode with the Inspector closed must still
  // allow her to open normally.
  const setOpenAuto = useCallback((value: boolean): SetOpenAutoResult => {
    if (value && mytaverseEventRef.current?.isEditorInspectorOpen) {
      return { opened: false, reason: 'isEditorInspectorOpen' };
    }
    setOpen(value);
    return { opened: true };
  }, []);

  const prevOpenRef = useRef(open);

  const userNameRef = useRef(userName);
  userNameRef.current = userName;

  const activeBrandingIdRef = useRef(activeBrandingId);
  activeBrandingIdRef.current = activeBrandingId;

  // The real "on join" region (a map region flagged isStartingPoint), falling
  // back to the synthetic 'landing' pseudo-trigger for maps without one.
  // Recomputed every render since mytaverseEventRef is a ref (no re-render on
  // mutation) — cheap, just a scan over the current map's region list.
  const joinTriggerName = resolveJoinTriggerName(
    mytaverseEventRef.current?.currentRoom?.map?.regions,
  );
  const joinTriggerNameRef = useRef(joinTriggerName);
  joinTriggerNameRef.current = joinTriggerName;

  // Child region (e.g. an exhibition booth) -> parent region name, for
  // scripts bound to the parent only. Recomputed every render for the same
  // reason as joinTriggerName above.
  const parentTriggerMap = buildParentTriggerMap(
    mytaverseEventRef.current?.currentRoom?.map?.regions,
  );
  const parentTriggerMapRef = useRef(parentTriggerMap);
  parentTriggerMapRef.current = parentTriggerMap;

  const prevActiveBrandingIdRef = useRef(activeBrandingId);

  const debugEventAiSettingsRef = useRef<IEventSettingsAiAssistant | null>(
    null,
  );

  // Shared forward-refs — resolved after hook calls below
  const linearScriptPlayerRef = useRef<LinearScriptPlayer | undefined>(
    undefined,
  );
  const enqueuedScriptIdRef = useRef<string | null>(null);
  // Set when the user leaves into an empty region while a finish_step script
  // with bridgeRegionGaps is still playing. The stop/UI-reset is deferred until
  // the current step's audio ends naturally (handled in useAssistantAudio).
  const pendingRegionClearRef = useRef<boolean>(false);
  const startFlowRef = useRef<StartFlowFn>(() => {});
  const stopPlaybackRef = useRef<() => void>(() => {});
  const preRenderedAudioRef = useRef<
    {
      text: string;
      audio: null | ArrayBuffer;
      voiceId: string;
      engine: string;
      language: string;
      area: string;
      index: number;
    }[]
  >([]);
  const resetAssistantUIRef = useRef<() => void>(() => {});
  const generatePollyAudioRef = useRef<GeneratePollyAudioFn>(async () => {});
  const cbOnCompletedStepRef = useRef<CbOnCompletedStepFn>(() => {});
  const playBlockAudioRef = useRef<PlayBlockAudioFn>(async () => {});
  const startAudioPrerenderRef = useRef<StartAudioPrerenderFn>(async () => {});
  const scriptTransitionRef = useRef<ScriptTransitionCallbacks>({
    setIsScriptCompleted: () => {},
    setPendingScriptStart: () => {},
    setIsScriptTransitioning: () => {},
  });

  if (!mytaverseEventRef.current) {
    mytaverseEventRef.current = {} as any;
  }

  // useRegionHandler reads this from inside a stable callback, so it needs the
  // ref; the value itself comes in as a prop (see ProviderProps) so an
  // EVENT_SETTINGS_UPDATED broadcast actually re-renders this provider.
  const isAskMeAnythingEnabledRef = useRef(isAskMeAnythingEnabled);
  isAskMeAnythingEnabledRef.current = isAskMeAnythingEnabled;

  const getEffectiveAiSettings: GetEffectiveAiSettingsFn = () => {
    const mytaverseEvent = mytaverseEventRef.current;
    let eventAiSettings: IEventSettingsAiAssistant | undefined;

    if (mytaverseEvent?.currentEventId) {
      eventAiSettings =
        mytaverseEvent.currentEvent?.settings?.aiAssistant ??
        mytaverseEvent.events?.find(
          (event: any) => event.eventId === mytaverseEvent.currentEventId,
        )?.settings?.aiAssistant;
    } else if (debugEventId) {
      eventAiSettings = debugEventAiSettingsRef.current ?? undefined;
    }

    const language = eventAiSettings?.language || defaultAiSettings.language;
    const baseLanguage = eventAiSettings?.baseLanguage || language;

    // The configuration the room runs may name its own voice per language.
    // Only the voices are its to override — which languages exist stays with
    // the project, since that is what the scripts were translated against.
    const brandingVoices = mytaverseEvent?.currentBranding?.assistantVoices;
    const voicesByLanguage = brandingVoices
      ? { ...eventAiSettings?.voicesByLanguage, ...brandingVoices }
      : eventAiSettings?.voicesByLanguage;
    const baseVoice = brandingVoices?.[baseLanguage];

    return {
      engine:
        baseVoice?.engine ||
        eventAiSettings?.engine ||
        defaultAiSettings.engine,
      voiceId:
        baseVoice?.voiceId ||
        eventAiSettings?.voiceId ||
        defaultAiSettings.voiceId,
      language,
      baseLanguage,
      languages: eventAiSettings?.languages,
      voicesByLanguage,
    };
  };

  // ---- Hooks (order matters: chat before progress so we can pass its setter) ----

  const navigation = useAssistantNavigation();

  const activeRegionNameRef = useRef<string | undefined>(undefined);

  // chat has no dependencies on other hooks — initialize before progress
  const chat = useAssistantChat({
    mytaverseEventRef,
    activeBrandingIdRef,
    generatePollyAudioRef,
    getEffectiveAiSettings,
    activeRegionNameRef,
  });

  // React state setters are stable references, safe to pass directly to progress
  const progress = useAssistantProgress({
    mytaverseEventRef,
    linearScriptPlayerRef,
    stopPlaybackRef,
    enqueuedScriptIdRef,
    resetAssistantUIRef,
    setAwaitingResponseType: chat.setAwaitingResponseType,
    activeBrandingIdRef,
  });

  const scripts = useAssistantScripts({
    mytaverseEventRef,
    activeBrandingIdRef,
    selectedLanguageRef,
    preRenderedAudioRef,
    debugEventAiSettingsRef,
    userNameRef,
    getEffectiveAiSettings,
  });

  const audio = useAssistantAudio({
    openRef,
    setOpen: setOpenAuto,
    linearScriptPlayerRef,
    enqueuedScriptIdRef,
    scriptsRef: scripts.scriptsRef,
    startFlowRef,
    stopPlaybackRef,
    preRenderedAudioRef,
    scriptTransitionRef,
    pendingRegionClearRef,
    resetAssistantUIRef,
    getEffectiveAiSettings,
  });

  const flow = useScriptFlow({
    scriptsRef: scripts.scriptsRef,
    progressState: progress.progressState,
    sessionProgressRef: progress.sessionProgressRef,
    linearScriptPlayerRef,
    playBlockAudioRef,
    cbOnCompletedStepRef,
    resetAssistantUIRef,
    mytaverseUserRef,
    mytaverseEventRef,
    userNameRef,
    selectedLanguageRef,
    selectedLanguage,
    preRenderedAudioRef,
    isAudioPlaybackActiveRef: audio.isAudioPlaybackActiveRef,
    openRef,
    setOpen: setOpenAuto,
    startAudioPrerenderRef,
    startFlowRef,
    getEffectiveAiSettings,
  });

  const region = useRegionHandler({
    linearScriptPlayerRef,
    isAudioPlaybackActiveRef: audio.isAudioPlaybackActiveRef,
    scriptsRef: scripts.scriptsRef,
    progressState: progress.progressState,
    sessionProgressRef: progress.sessionProgressRef,
    enqueuedScriptIdRef,
    pendingRegionClearRef,
    stopPlaybackRef,
    startFlowRef,
    setOpen,
    isAskMeAnythingEnabledRef,
    joinTriggerNameRef,
    parentTriggerMapRef,
    setActiveScreen: navigation.setActiveScreen,
    setNavigationHistory: navigation.setNavigationHistory,
    setIsScriptCompleted: flow.setIsScriptCompleted,
    setIsScriptTransitioning: flow.setIsScriptTransitioning,
    setIsScriptQuizActive: flow.setIsScriptQuizActive,
    setIsDisplayUserFeedback: chat.setIsDisplayUserFeedback,
    setCanUserSkip: flow.setCanUserSkip,
    setPendingScriptStart: flow.setPendingScriptStart,
  });

  // ---- Wire forward-refs every render ----

  stopPlaybackRef.current = audio.stopPlayback;
  startFlowRef.current = flow.startFlow;
  generatePollyAudioRef.current = audio.generatePollyAudio;
  cbOnCompletedStepRef.current = progress.cbOnCompletedStep;
  playBlockAudioRef.current = audio.playBlockAudio;
  startAudioPrerenderRef.current = audio.startAudioPrerender;

  scriptTransitionRef.current = {
    setIsScriptCompleted: flow.setIsScriptCompleted,
    setPendingScriptStart: flow.setPendingScriptStart,
    setIsScriptTransitioning: flow.setIsScriptTransitioning,
  };

  const resetAssistantUI = useCallback(() => {
    globals.activeScreenGlob = SCREENS.DEFAULT;
    navigation.setActiveScreen(SCREENS.DEFAULT);
    navigation.setNavigationHistory([SCREENS.DEFAULT]);
    flow.setIsScriptCompleted(false);
    flow.setIsScriptTransitioning(false);
    flow.setIsScriptQuizActive(false);
    chat.setIsDisplayUserFeedback(false);
    flow.setCanUserSkip(false);
    flow.setPendingScriptStart(null);
  }, []);
  resetAssistantUIRef.current = resetAssistantUI;

  const setIsMuted = useCallback(() => {
    setIsMutedLoc((prev) => {
      globals.isMutedGlob = !prev;
      return !prev;
    });
  }, []);

  const teleportToRegion = useCallback(
    (regionName: string) =>
      teleportToRegionFn(regionName, sendJSONMessageToWebSocketRef),
    [],
  );

  // Editing a script while it's the one currently loaded in the player used to
  // have no visible effect until the user left and re-entered the
  // trigger/spot — fetchScripts() refreshes scriptsRef, but the already-built
  // LinearScriptPlayer keeps running on the stale nodes it was constructed
  // from. If nothing is actively narrating right now (paused, or idle between
  // steps), it's safe to silently rebuild the player from the fresh script
  // data at the same step — audio IS actively playing is the one case left
  // alone, matching the existing "cycle out of the region" behavior.
  //
  // isAudioPlaybackActiveRef is NOT the right signal here: pauseAudioPlayback()
  // (the dedicated Play/Pause control, and the mute button) only pauses the
  // AudioVisualizer — it never touches isAudioPlaybackActiveRef, which stays
  // true until the audio either ends naturally or stopPlayback() runs. Using
  // it here would treat "explicitly paused" as "still playing" and skip the
  // reload — exactly the case this fix is for. globals.visualizerInstance's
  // own isPlaying() reflects whether it's actually emitting sound right now.
  const reloadActiveScriptIfIdle = useCallback(() => {
    const player = linearScriptPlayerRef.current;
    const playerState = player?.getCurrentState();
    const isActuallyPlaying = !!globals.visualizerInstance?.isPlaying?.();

    if (!playerState?.isRunning || isActuallyPlaying || !playerState.name) {
      return;
    }

    const currentIndex = player?.getCurrentIndex() ?? 0;
    const trigger = playerState.name;

    stopPlaybackRef.current();
    player?.stop();
    startFlowRef.current(trigger, {
      startStep: currentIndex + 1,
      bypassPlaybackPolicy: true,
    });
  }, []);

  const fetchScriptsAndReload = useCallback(async () => {
    const newScripts = await scripts.fetchScripts();
    scripts.scriptsRef.current = newScripts;
    reloadActiveScriptIfIdle();
    return newScripts;
  }, [reloadActiveScriptIfIdle]);

  // ---- Effects ----

  useEffect(() => {
    globals.isMutedGlob = false;
    setIsMutedLoc(false);
    // Re-arm the assistant for this mount — a previous mount may have left
    // this set via stopAssistantAudio() when the last event page was left.
    globals.assistantTornDown = false;
  }, []);

  useEffect(() => {
    initDebugSendFn((msg) => sendJSONMessageToWebSocketRef.current(msg));
    return () => initDebugSendFn(() => {});
  }, []);

  // Reset the assistant's own session UI whenever the panel is closed, so
  // reopening always starts fresh — only chat history survives a close.
  // Covers every close path that flips `open` to false, not just a single
  // button handler. Saved progress (progress.progressState /
  // sessionProgressRef, and anything already persisted via saveQuizAnswers)
  // is deliberately left untouched here — that's the participant's persisted
  // completion record, not part of the current UI session. spotTriggerType /
  // isSpotImageModalOpen are also left alone — those belong to
  // SpotChoiceModalBridge / SpotFullscreenViewBridge, separate in-world spot
  // viewers that share this context but aren't part of the assistant panel.
  useEffect(() => {
    if (prevOpenRef.current && !open) {
      if (linearScriptPlayerRef.current?.getCurrentState().isRunning) {
        linearScriptPlayerRef.current.stop();
      }
      stopPlaybackRef.current();
      enqueuedScriptIdRef.current = null;
      resetAssistantUI();
      chat.setAwaitingResponseType(AWAITING_RESPONSE_TYPE.NONE);
      setIsRecording(false);
    }

    prevOpenRef.current = open;
  }, [open, resetAssistantUI, chat.setAwaitingResponseType]);

  // MV-1816: an admin can turn "Ask Me Anything" off while a participant is
  // sitting on the chat screen. Nothing but DefaultScreen gates AMA — the chat
  // screen itself has no such check — so without this the free-form Q&A stays
  // fully usable until the panel is closed. Send the panel back to the default
  // screen on the enabled -> disabled transition.
  //
  // Deliberately NOT resetAssistantUI(): that also clears the script-flow flags
  // (pendingScriptStart, canUserSkip, isScriptCompleted), and a script can be
  // mid-flow while the chat screen is up — a region walk-in starts a script
  // without changing the active screen — so dropping a pending script start
  // here would leave it unstartable. Only the navigation part is wanted, the
  // same three lines useRegionHandler uses for its own return-to-default.
  const prevIsAskMeAnythingEnabledRef = useRef(isAskMeAnythingEnabled);

  useEffect(() => {
    const wasEnabled = prevIsAskMeAnythingEnabledRef.current;
    prevIsAskMeAnythingEnabledRef.current = isAskMeAnythingEnabled;

    if (!wasEnabled || isAskMeAnythingEnabled) {
      return;
    }

    if (navigation.activeScreen !== SCREENS.CHAT) {
      return;
    }

    log('[ama] Ask Me Anything disabled while the chat screen was open');
    globals.activeScreenGlob = SCREENS.DEFAULT;
    navigation.setActiveScreen(SCREENS.DEFAULT);
    navigation.setNavigationHistory([SCREENS.DEFAULT]);
  }, [
    isAskMeAnythingEnabled,
    navigation.activeScreen,
    navigation.setActiveScreen,
    navigation.setNavigationHistory,
  ]);

  useEffect(() => {
    if (objectPoiId === null) {
      resetAssistantUI();
      return;
    }

    if (!objectPoiId) {
      return;
    }

    const script = scripts.scriptsRef.current.find(
      (s) =>
        s.triggerType === 'object' &&
        (Array.isArray(s.scriptTrigger)
          ? s.scriptTrigger.includes(objectPoiId)
          : s.scriptTrigger === objectPoiId),
    );

    if (!script) {
      return;
    }

    startFlowRef.current(objectPoiId, { scriptId: script.scriptId });
  }, [objectPoiId, resetAssistantUI]);

  const effectiveRegionName =
    region.simulatedRegionOverride !== undefined
      ? region.simulatedRegionOverride
      : currentRegionName;

  activeRegionNameRef.current = effectiveRegionName;

  useEffect(() => {
    region.handleRegionChange(effectiveRegionName ?? '');
  }, [effectiveRegionName]);

  // Editor Mode's "Teleport to trigger" renders outside this provider's tree
  // (see MytaverseEventProvider.pendingAssistantRegionTrigger), so it can't
  // call triggerRegionChange from context directly. It bumps the nonce here
  // instead; replay the region change exactly once per nonce.
  const lastRegionTriggerNonceRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (
      !pendingAssistantRegionTrigger ||
      pendingAssistantRegionTrigger.nonce === lastRegionTriggerNonceRef.current
    ) {
      return;
    }

    lastRegionTriggerNonceRef.current = pendingAssistantRegionTrigger.nonce;
    log('[region] replaying pendingAssistantRegionTrigger', {
      trigger: pendingAssistantRegionTrigger.trigger,
      nonce: pendingAssistantRegionTrigger.nonce,
      hasContent: pendingAssistantRegionTrigger.hasContent,
    });
    // This only ever fires from an explicit Editor Mode teleport action (see
    // the comment above) — a deliberate admin request to preview that
    // trigger's script, not an incidental walk-in. When the caller has
    // determined the destination actually has content (a script with real
    // steps, or matching knowledge — see hasAssistantContentForTrigger), it
    // must always open, even in branding mode: setOpenAuto's branding-mode
    // guard (below) exists to stop scripts from popping Noa up unprompted
    // while someone is mid-edit, which doesn't apply here. openRef is updated
    // synchronously (not just via setOpen, which only lands on next render)
    // so the ui_click branch's `if (!openRef.current)` check in
    // useScriptFlow sees it as already open and skips setOpenAuto's guard
    // entirely for this call. When there's no content, skip the forced open
    // entirely — handleRegionChange below still runs, so an empty
    // destination falls through to the same "no script" close/leave-alone
    // logic a real walk-in would hit, instead of popping an empty panel.
    if (pendingAssistantRegionTrigger.hasContent) {
      openRef.current = true;
      setOpen(true);
    }
    region.handleRegionChange(pendingAssistantRegionTrigger.trigger);
  }, [pendingAssistantRegionTrigger]);

  useAsyncEffect(async () => {
    await Promise.all([
      scripts.fetchScripts(),
      progress.fetchParticipantProgress(),
    ]);
  }, []);

  useEffect(() => {
    if (!assistantScriptUpdateTick) {
      return;
    }

    audio.clearPrerenderedAudio();
    fetchScriptsAndReload();
  }, [assistantScriptUpdateTick, fetchScriptsAndReload]);

  useEffect(() => {
    if (prevActiveBrandingIdRef.current === activeBrandingId) {
      return;
    }

    prevActiveBrandingIdRef.current = activeBrandingId;
    resetAssistantUI();
    progress.sessionProgressRef.current = {};
    // Clear before refetching — fetchParticipantProgress merges progressState.current
    // in as "local unsynced progress", and leftover data from the old branding
    // would otherwise leak into the newly loaded branding's progress.
    progress.progressState.current = '{}';
    audio.clearPrerenderedAudio();

    let cancelled = false;

    (async () => {
      try {
        const [newScripts] = await Promise.all([
          scripts.fetchScripts(),
          // Progress is scoped by activeBrandingId — without this, progressState
          // (and anything reading it, e.g. the Visited Areas pill) keeps showing
          // whichever branding was active when it was last loaded.
          progress.fetchParticipantProgress(),
        ]);

        if (cancelled) {
          return;
        }

        scripts.scriptsRef.current = newScripts;
        region.handleRegionChange(effectiveRegionName ?? '');
      } catch (error) {
        log('[branding] failed to refresh scripts/progress', {
          brandingId: activeBrandingId,
          error: getCatchErrorMessage(error),
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activeBrandingId, resetAssistantUI]);

  // ---- Derived values ----

  const currentTag = effectiveRegionName || undefined;

  const filteredChatHistory = useMemo(() => {
    return chat.chatHistory.filter((m) => {
      if (activeBrandingId && m.brandingId !== activeBrandingId) {
        return false;
      }
      if (m.tag !== currentTag) {
        return false;
      }
      return true;
    });
  }, [chat.chatHistory, activeBrandingId, currentTag]);

  // ---- Context value ----

  const contextValue = useMemo(
    () => ({
      activeScreen: navigation.activeScreen,
      setActiveScreen: navigation.setActiveScreen,
      open,
      setOpen,
      handleSendRequest: chat.handleSendRequest,
      chatHistory: filteredChatHistory,
      navigate: navigation.navigate,
      setNavigationHistory: navigation.setNavigationHistory,
      awaitingResponseType: chat.awaitingResponseType,
      stopPlayback: audio.stopPlayback,
      isRecording,
      setIsRecording,
      audioPromptHistory,
      isMuted,
      setIsMuted,
      teleportToRegion,
      startFlow: flow.startFlow,
      simulateRegionChange: region.simulateRegionChange,
      triggerRegionChange: region.handleRegionChange,
      linearScriptPlayerRef,
      isAudioPlaybackActiveRef: audio.isAudioPlaybackActiveRef,
      isAudioPlaybackActive: audio.isAudioPlaybackActive,
      scripts: scripts.scripts,
      isDisplayUserFeedback: chat.isDisplayUserFeedback,
      setIsDisplayUserFeedback: chat.setIsDisplayUserFeedback,
      handleSubmitUserFeedback: chat.handleSubmitUserFeedback,
      fetchScripts: fetchScriptsAndReload,
      fetchParticipantProgress: progress.fetchParticipantProgress,
      resetProgress: progress.resetProgress,
      saveQuizAnswers: progress.saveQuizAnswers,
      clearPrerenderedAudio: audio.clearPrerenderedAudio,
      isScriptQuizActive: flow.isScriptQuizActive,
      setIsScriptQuizActive: flow.setIsScriptQuizActive,
      audioEndTick: audio.audioEndTick,
      isScriptCompleted: flow.isScriptCompleted,
      setIsScriptCompleted: flow.setIsScriptCompleted,
      isScriptTransitioning: flow.isScriptTransitioning,
      canUserSkip: flow.canUserSkip,
      pendingScriptStart: flow.pendingScriptStart,
      confirmScriptStart: flow.confirmScriptStart,
      resetAssistantUI,
      userName: userName || '',
      activeRegionName: effectiveRegionName ?? '',
      currentMapName: assistantMapKey(
        mytaverseEventRef.current?.currentRoom?.map,
      ),
      joinTriggerName,
      activeBrandingId:
        mytaverseEventRef.current?.currentRoom?.activeBrandingId ||
        activeBrandingId ||
        undefined,
      availableLanguages:
        getEffectiveAiSettings().languages ?? defaultAvailableLanguages,
      audioLanguage,
      setAudioLanguage,
      isPollyAudioLoading: audio.isPollyAudioLoading,
      spotTriggerType,
      setSpotTriggerType,
      isSpotImageModalOpen,
      setIsSpotImageModalOpen,
      setPlaybackRate: audio.setPlaybackRate,
      getAudioProgress: audio.getAudioProgress,
      pauseAudioPlayback: audio.pauseAudioPlayback,
      resumeAudioPlayback: audio.resumeAudioPlayback,
      progressStateRef: progress.progressState,
      enqueuedScriptIdRef,
    }),
    [
      navigation.activeScreen,
      open,
      chat.handleSendRequest,
      filteredChatHistory,
      navigation.navigate,
      chat.awaitingResponseType,
      audio.stopPlayback,
      isRecording,
      audioPromptHistory,
      isMuted,
      setIsMuted,
      teleportToRegion,
      flow.startFlow,
      region.simulateRegionChange,
      region.handleRegionChange,
      audio.isAudioPlaybackActive,
      scripts.scripts,
      chat.isDisplayUserFeedback,
      chat.handleSubmitUserFeedback,
      fetchScriptsAndReload,
      progress.fetchParticipantProgress,
      progress.resetProgress,
      progress.saveQuizAnswers,
      audio.clearPrerenderedAudio,
      flow.isScriptQuizActive,
      audio.audioEndTick,
      flow.isScriptCompleted,
      flow.isScriptTransitioning,
      flow.canUserSkip,
      flow.pendingScriptStart,
      flow.confirmScriptStart,
      resetAssistantUI,
      userName,
      effectiveRegionName,
      audioLanguage,
      audio.isPollyAudioLoading,
      spotTriggerType,
      isSpotImageModalOpen,
      joinTriggerName,
      audio.setPlaybackRate,
      audio.getAudioProgress,
      audio.pauseAudioPlayback,
      audio.resumeAudioPlayback,
    ],
  );

  return (
    <AssistantContext.Provider value={contextValue}>
      {children}
    </AssistantContext.Provider>
  );
};

export const AssistantProvider = memo(AssistantProviderInner);
